import { ListaDadosAcaoLinha, ListaDadosColuna } from '@app/components/lista-dados/lista-dados.component';
import { deburr } from 'lodash';
import { HttpClient } from '@angular/common/http';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { DialogData } from 'src/app/models/dialog-data';
import { DialogComponent } from 'src/app/components/dialog/dialog.component';
import { BeVeiculo } from 'src/app/models/BeVeiculo';
import { SubService } from 'src/app/services/sub.service';
import { SnackBarService } from 'src/app/services/snack-bar.service';
import { BeVeiculoXUsuario } from 'src/app/models/BeVeiculoXUsuario';
import { BeUsuario } from 'src/app/models/BeUsuario';
import { TokenService } from '@app/services/token.service';
import { Router } from '@angular/router';
import { DialogSelectUsuarioComponent } from '@app/components/dialog-select-usuario/dialog-select-usuario.component';

interface BeVeiculoXUsuarioExtended extends BeVeiculoXUsuario {
  editing?: boolean;
  originalValues?: BeVeiculoXUsuario;
}

@Component({
  selector: 'app-veiculo-x-usuarios',
  templateUrl: './list-veiculo-x-usuarios.component.html',
  styleUrls: ['./list-veiculo-x-usuarios.component.css']
})
export class ListVeiculoXUsuariosComponent implements OnInit, OnDestroy {
  @Input() public veiculo?: BeVeiculo;

  public dados: BeVeiculoXUsuarioExtended[] = [];
  public subscription?: Subscription;
  public listaUsuario: BeUsuario[] = [];
  public isProprietario: boolean | undefined = false;
  public currentUserCpf?: string;
  public filtrarLinha = (linha: BeVeiculoXUsuarioExtended, filtro: string): boolean =>
    [linha.usuario?.noNomeCompleto, linha.usuario?.noCpfj].some(valor =>
      deburr(valor ?? '').toLowerCase().includes(deburr(filtro).toLowerCase()));

  public colunas: ListaDadosColuna[] = [
    { campo: 'cpf', rotulo: 'CPF', tamanho: '130px', editavel: false,
      formatador: linha => linha.usuario?.noCpfj ?? this.listaUsuario.find(u => u.idUsuario === linha.idUsuario)?.noCpfj ?? '' },
    { campo: 'idUsuario', rotulo: 'Nome', tamanho: '240px',
      formatador: linha => linha.usuario?.noNomeCompleto ?? this.listaUsuario.find(u => u.idUsuario === linha.idUsuario)?.noNomeCompleto ?? '',
      icone: linha => linha.blProprietario ? 'directions_car' : undefined,
      iconeSecundario: linha => linha.blProprietario ? 'person' : undefined,
      edicao: { tipoEditor: 'combobox', opcoes: [], campoChave: 'idUsuario', campoRotulo: 'rotulo', validacao: { obrigatorio: true } } },
  ];
  public podeExcluir = (linha: BeVeiculoXUsuarioExtended): boolean =>
    !!(this.isProprietario || this.isUsuarioAtual(linha)) && !linha.blProprietario;
  public acoesLinha: ListaDadosAcaoLinha[] = [
    { chave: 'trocarProprietario', texto: 'Trocar Proprietário', icone: 'social_distance',
      visivel: linha => !!this.isProprietario && linha.blProprietario && this.dados.length > 1 }
  ];

  public constructor(
    private httpClient: HttpClient,
    private sub: SubService,
    private dialog: MatDialog,
    private snackBarService: SnackBarService,
    private tokenService: TokenService,
    private router: Router
  ) { }

  public ngOnInit(): void {
    if (!this.veiculo) {
      this.snackBarService.exibirErro('Nenhum veículo selecionado');
      return;
    }
    this.loadUsuarios();
    this.onResult(this.veiculo.listaVeiculoXUsuarios);
    this.verificarProprietario();
  }

  public ngOnDestroy(): void {
    this.unsubscribe();
  }

  public onIniciarEdicaoLinha(linha: BeVeiculoXUsuarioExtended): void {
    linha.idVeiculo = this.veiculo!.idVeiculo!;
    linha.blProprietario = false;
  }

  public onSalvarLinha(veiculoXUsuario: BeVeiculoXUsuarioExtended) {
    this.unsubscribe();

    const data: BeVeiculoXUsuario = {
      idVeiculo: veiculoXUsuario.idVeiculo,
      idUsuario: veiculoXUsuario.idUsuario,
      blProprietario: false
    };

    if (veiculoXUsuario.idVeiculoXUsuario) {
      data.idVeiculoXUsuario = veiculoXUsuario.idVeiculoXUsuario;
      this.subscription = this.httpClient.put<BeVeiculoXUsuario>('/api/VeiculoXUsuarios/', data).subscribe(this.getCallback(veiculoXUsuario, false));
    } else {
      this.subscription = this.httpClient.post<BeVeiculoXUsuario>('/api/VeiculoXUsuarios/', data).subscribe(this.getCallback(veiculoXUsuario, true));
    }
  }

  public onDelete(veiculoXUsuario: BeVeiculoXUsuarioExtended) {
    this.showDialog('Deseja excluir esse registro?', 'Alerta', 'Não', 'Sim', veiculoXUsuario);
  }

  public onResult(res?: BeVeiculoXUsuario[]): void {
    this.dados = res ?? [];
  }

  public showDialog(msg: string, statusMsg: string, button: string = 'OK', secondButton: string = '', veiculoXUsuario: BeVeiculoXUsuarioExtended = {} as BeVeiculoXUsuarioExtended) {
    if (this.subscription)
      return;
    const dialogData: DialogData = {
      title: statusMsg,
      message: msg,
      buttonText: button,
      secondButtonText: secondButton,
    };
    const dialogRef = this.dialog.open(DialogComponent, { data: dialogData });
    dialogRef.afterClosed().subscribe((result) => {
      this.unsubscribe();
      if (result) {
        this.sub.reset(this.httpClient.delete(`/api/VeiculoXUsuarios/${veiculoXUsuario.idVeiculoXUsuario}`),
          () => {
            this.onDeleteConfirm(veiculoXUsuario)
            if (!this.isProprietario)
              this.router.navigate(['/veiculos']);
          },
          () => this.showDialog('Não foi possível excluir o registro!', 'Erro')
        );
      }
    })
  }

  public onTrocarProprietario() {
    if (!this.veiculo?.idVeiculo) {
      this.snackBarService.exibirErro('Nenhum veículo selecionado');
      return;
    }

    this.subscription = this.httpClient.get<BeUsuario[]>(
      `/api/Usuarios/ListaUsuariosDoVeiculo/${this.veiculo.idVeiculo}`
    ).subscribe({
      next: (usuarios) => {
        this.unsubscribe();
        
        if (usuarios.length < 1) {
          this.snackBarService.exibirErro('É necessário ter pelo menos 2 usuários no veículo para trocar o proprietário');
          return;
        }

        this.abrirDialogoTrocarProprietario(usuarios);
      },
      error: (err) => {
        this.unsubscribe();
        this.snackBarService.exibirErro(`Erro ao carregar usuários - ${err.status} - ${err.statusText}`);
      }
    });
  }

  private abrirDialogoTrocarProprietario(usuarios: BeUsuario[]) {
    const dialogRef = this.dialog.open(DialogSelectUsuarioComponent, {
      data: {
        title: 'Selecionar Novo Proprietário',
        usuarios: usuarios,
        message: 'Selecione o usuário que será o novo proprietário do veículo:'
      },
      width: '400px'
    });

    dialogRef.afterClosed().subscribe((idUsuarioSelecionado: number) => {
      if (idUsuarioSelecionado) {
        this.trocarProprietario(idUsuarioSelecionado);
      }
    });
  }

  private trocarProprietario(idUsuario: number) {
    if (!this.veiculo?.idVeiculo) {
      return;
    }

    const data = {
      idVeiculo: this.veiculo.idVeiculo,
      idUsuario: idUsuario,
      blProprietario: true
    };

    this.subscription = this.httpClient.post<BeVeiculoXUsuario[]>(
      `/api/VeiculoXUsuarios/AtualizarBlProprietario`,
      data
    ).subscribe({
      next: (res: BeVeiculoXUsuario[]) => {
        this.unsubscribe();

        res.forEach((usuarioAtualizado) => {
          const usuario = this.dados.find(u => u.idVeiculoXUsuario === usuarioAtualizado.idVeiculoXUsuario);
          if (usuario) {
            usuario.blProprietario = usuarioAtualizado.blProprietario;
          }
        });

        this.verificarProprietario();
        this.snackBarService.exibirSucesso('Proprietário alterado com sucesso!');
        this.dados = [...this.dados];
      },
      error: (err) => {
        this.unsubscribe();
        this.snackBarService.exibirErro(`Erro ao trocar proprietário - ${err.status} - ${err.statusText}`);
      }
    });
  }

  public isUsuarioAtual(veiculoXUsuario: BeVeiculoXUsuarioExtended): boolean {
    return veiculoXUsuario.usuario?.noCpfj === this.currentUserCpf;
  }

  private loadUsuarios(): void {
    if (!this.veiculo?.idVeiculo) {
      return;
    }
    
    this.httpClient.get<BeUsuario[]>(`/api/Usuarios/ListaUsuariosPorVeiculo/${this.veiculo.idVeiculo}`).subscribe({
      next: (usuarios) => {
        this.listaUsuario = usuarios;
        this.colunas[1].edicao!.opcoes = usuarios.map(usuario => ({ ...usuario, rotulo: `${usuario.noNomeCompleto} - ${usuario.noCpfj}` }));
      },
      error: (err) => {
        this.snackBarService.exibirErro('Erro ao carregar lista de usuários');
        console.error('Error loading usuarios', err);
      }
    });
  }

  private getCallback(veiculoXUsuario: BeVeiculoXUsuarioExtended, isPost = false) {
    return {
      next: (res: BeVeiculoXUsuario) => {
        this.snackBarService.exibirSucesso(`Registro ${isPost ? 'criado' : 'alterado'}!`);
        if (isPost) {
          veiculoXUsuario.idVeiculoXUsuario = res.idVeiculoXUsuario;
        }
        veiculoXUsuario.usuario = this.listaUsuario.find(u => u.idUsuario === veiculoXUsuario.idUsuario);
        this.dados = [...this.dados];
        this.verificarProprietario();
        this.unsubscribe();
      },
      error: (err: any) => {
        this.snackBarService.exibirErro(`${err.error} - Erro ${err.status} - ${err.statusText}`);
        this.unsubscribe();
      }
    };
  }

  private unsubscribe() {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = undefined;
    }
  }

  private onDeleteConfirm(res: BeVeiculoXUsuarioExtended) {
    this.dados.splice(this.dados.indexOf(res), 1);
    this.dados = [...this.dados];
    this.snackBarService.exibirSucesso('Registro excluído!');
  }

  private verificarProprietario() {
    this.tokenService
      .getTokenUserCredentials()
      .subscribe((token) =>
        this.tokenService
          .decodeToken(token)
          .subscribe((authToken) => {
            this.currentUserCpf = authToken!.cpf;
            this.isProprietario = this.dados.some(a => a.usuario?.noCpfj === this.currentUserCpf && a.blProprietario);
          })
      );
  }
}
